import { useState } from 'react'
import { ChevronDown, ChevronRight } from 'lucide-react'
import { cn } from '@/utils/cn'
import type {
  SeasonEventDispositionGroup,
  SeasonEventDispositionResult,
  SeasonEventDispositionRow,
} from './seasonEventDispositionMockData'

interface Props {
  data: SeasonEventDispositionResult
}

function formatNumber(value: number): string {
  return Math.round(value).toLocaleString('en-US')
}

function formatPercent(value: number): string {
  return `${(value * 100).toFixed(1)}%`
}

// Zebra stripe is applied by absolute rendered row index (not by depth or
// bold-ness) — confirmed via pixel sampling of the reference screenshot.
function zebraBg(index: number): string {
  return index % 2 === 0 ? 'bg-white' : 'bg-[#ECEAEA]'
}

export function SeasonEventDispositionTable({ data }: Props) {
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set())

  const toggle = (key: string) => {
    setCollapsed(prev => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  let rowIndex = 0
  const nextRowIndex = () => rowIndex++

  return (
    <div className="overflow-hidden rounded-lg border border-border bg-white shadow-sm">
      <div className="max-h-[560px] overflow-auto">
        <table
          className="w-full border-separate text-xs"
          style={{ borderSpacing: 0 }}
        >
          <thead>
            <tr>
              <th className="sticky top-0 z-10 border-r border-b border-border bg-white px-3 py-2 text-left font-semibold text-foreground" />
              <th className="sticky top-0 z-10 border-b border-border bg-white px-3 py-2 text-right font-semibold text-foreground">
                Metric Base
              </th>
              <th className="sticky top-0 z-10 border-b border-border bg-white px-3 py-2 text-right font-semibold text-foreground">
                Metric % of Total Base
              </th>
              <th className="sticky top-0 z-10 border-b border-border bg-white px-3 py-2 text-right font-semibold text-foreground">
                Metric Compare To
              </th>
              <th className="sticky top-0 z-10 border-b border-border bg-white px-3 py-2 text-right font-semibold text-foreground">
                Metric % of Total Compare To
              </th>
            </tr>
          </thead>
          <tbody>
            {data.groups.map(group => (
              <GroupBlock
                key={group.key}
                group={group}
                isCollapsed={collapsed.has(group.key)}
                onToggle={() => toggle(group.key)}
                nextRowIndex={nextRowIndex}
              />
            ))}
            <TotalRow row={data.total} rowIndex={nextRowIndex()} />
          </tbody>
        </table>
      </div>
    </div>
  )
}

function GroupBlock({
  group,
  isCollapsed,
  onToggle,
  nextRowIndex,
}: {
  group: SeasonEventDispositionGroup
  isCollapsed: boolean
  onToggle: () => void
  nextRowIndex: () => number
}) {
  const headerRowIndex = nextRowIndex()
  return (
    <>
      <tr
        onClick={onToggle}
        className={cn(
          'cursor-pointer font-semibold hover:brightness-95',
          zebraBg(headerRowIndex)
        )}
      >
        <td className="border-r border-border px-3 py-1.5">
          <span className="inline-flex items-center gap-1">
            {isCollapsed ? (
              <ChevronRight className="h-3 w-3 shrink-0" />
            ) : (
              <ChevronDown className="h-3 w-3 shrink-0" />
            )}
            {group.label}
          </span>
        </td>
        <td className="px-3 py-1.5 text-right tabular-nums">
          {formatNumber(group.metricBase)}
        </td>
        <td className="px-3 py-1.5 text-right tabular-nums">
          {formatPercent(group.metricPctOfTotalBase)}
        </td>
        <td className="px-3 py-1.5" />
        <td className="px-3 py-1.5" />
      </tr>
      {!isCollapsed &&
        group.leaves.map(leaf => (
          <LeafRowView key={leaf.key} row={leaf} rowIndex={nextRowIndex()} />
        ))}
    </>
  )
}

function LeafRowView({
  row,
  rowIndex,
}: {
  row: SeasonEventDispositionRow
  rowIndex: number
}) {
  return (
    <tr className={zebraBg(rowIndex)}>
      <td className="border-r border-border px-3 py-1.5 pl-7 text-muted-foreground">
        {row.label}
      </td>
      <td className="px-3 py-1.5 text-right tabular-nums">
        {formatNumber(row.metricBase)}
      </td>
      <td className="px-3 py-1.5 text-right tabular-nums">
        {formatPercent(row.metricPctOfTotalBase)}
      </td>
      <td className="px-3 py-1.5" />
      <td className="px-3 py-1.5" />
    </tr>
  )
}

function TotalRow({
  row,
  rowIndex,
}: {
  row: SeasonEventDispositionRow
  rowIndex: number
}) {
  return (
    <tr
      className={cn(
        'border-t-2 border-border font-semibold',
        zebraBg(rowIndex)
      )}
    >
      <td className="border-r border-border px-3 py-1.5">{row.label}</td>
      <td className="px-3 py-1.5 text-right tabular-nums">
        {formatNumber(row.metricBase)}
      </td>
      <td className="px-3 py-1.5 text-right tabular-nums">
        {formatPercent(row.metricPctOfTotalBase)}
      </td>
      <td className="px-3 py-1.5" />
      <td className="px-3 py-1.5" />
    </tr>
  )
}
